import { Datetime, getClasses, properCase } from '@/utils';
import { ForwardRefExoticComponent, forwardRef, useCallback } from 'react';

import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { returnStatusDisplay } from '@/features/LostAndFoundHistory/utils/helpers';
import { useLostAndFoundHistory } from '@/features/LostAndFoundHistory';
import { useLostAndFoundHistoryDrawer } from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryDrawer';

const LostAndFoundHistoryTableRow = ({ id }: { id: string }, ref: ForwardRefExoticComponent<HTMLDivElement>): JSX.Element => {
  const [{ report, selected }, setState] = useLostAndFoundHistory(({ reports, selected }) => ({
    report: reports.get(id),
    selected: selected.has(id),
  }));
  const setLostAndFoundHistoryDrawer = useLostAndFoundHistoryDrawer(({ setState }) => setState);

  const onSelect = useCallback(
    (): void =>
      setState((current) => {
        const newSelected = new Map(current.selected);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.set(id, current.reports.get(id));
        return { ...current, selected: newSelected };
      }),
    [report, id, setState]
  );
  const onEdit = (): void => {
    setLostAndFoundHistoryDrawer((current) => ({
      ...current,
      show: true,
      data: report,
    }));
  };

  return (
    <SimpleTableRow id={id} className={getClasses(selected ? 'SELECTED' : undefined)} onDoubleClick={onEdit} ref={ref}>
      <SimpleTableCell.Select checked={selected} onChange={onSelect} />
      <SimpleTableCell name="TRIP_AIRPORT_CODE">{report.crewAirportCode ?? report.tripAirportCode}</SimpleTableCell>
      <SimpleTableCell name="CREATED_AT">{new Datetime().setAsUTC(report.createdAt).toLocaleDatetime().frontendDatetime}</SimpleTableCell>
      <SimpleTableCell name="DISPATCHER_NAME">
        {report.dispatcherId ? [report.dispatcherFirstName, report.dispatcherLastName].join(' ') : null}
      </SimpleTableCell>
      <SimpleTableCell name="TRIP_SERVICER_IATA_AIRLINE_CODE">{report.tripServicerIataAirlineCode}</SimpleTableCell>
      <SimpleTableCell name="TRIP_FLIGHT_NAME">
        {report.crewFlightName || report.tripLoopName || report.tripFlightNumber
          ? `${report.crewFlightName ?? (report.tripLoopName || report.tripFlightNumber.toString().padStart(4, '0'))}`
          : null}
      </SimpleTableCell>
      <SimpleTableCell name="VEHICLE_TRACKING_ID">{report?.driverId ? report.vehicleTrackingId : null}</SimpleTableCell>
      <SimpleTableCell name="DRIVER_NAME">
        {report.driverId ? [report.driverFirstName, report.driverLastName].join(' ') : null}
      </SimpleTableCell>
      <SimpleTableCell name="DESCRIPTION">{report.description}</SimpleTableCell>
      <SimpleTableCell name="RETURN_STATUS">{properCase(returnStatusDisplay(report.returnStatus))}</SimpleTableCell>
    </SimpleTableRow>
  );
};

export default forwardRef(LostAndFoundHistoryTableRow);
